/*
 * Governance Tile · Direction-of-travel chart · one node per event.
 *
 * IJ-433 · 2026-06-01 · GB-TILE-01 rebuild.  Counting starts at zero
 * where the axes meet.  Each error, miss, or skip in the scope window
 * is one descending node on the red line; each success (a Judge PASS)
 * is one climbing node on the green line.  The newest event sits
 * nearest zero, older events run outward.  Every node carries a native
 * SVG <title> hover with a short description of that event.
 *
 * Data source: summary.chart_events = { errors: [...], successes: [...] }
 * where each item is { seq, date, gb, desc }, newest first (seq 1 nearest
 * zero).  Sourced from the Atlas errors engine and the Judge PASS log so
 * the chart matches the severity and actor rows by construction (BR-27).
 */
import {
  CHART_TITLE_PREFIX,
  CHART_TITLE_ZERO_TAIL,
  CHART_TITLE_ACTIVE_TAIL_SUFFIX,
  CHART_TITLE_TOOLTIP,
  CHART_EXPLAINER_STRONG,
  CHART_EXPLAINER_BODY,
  CHART_EMPTY_STATE_TITLE,
  CHART_EMPTY_STATE_BODY,
  CHART_Y_LABEL_POS,
  CHART_Y_LABEL_NEG,
  CHART_X_LABEL,
  CHART_ZERO_LABEL,
} from './copy/directionChart';

const VB_W = 900;
const VB_H = 570;
const PLOT_X_MIN = 60;
const PLOT_X_MAX = 880;
const PLOT_Y_TOP = 20;
const PLOT_Y_BOT = 460;
const ZERO_Y = 240;

const ERROR_COLOUR = '#ef4444';
const SUCCESS_COLOUR = '#22c55e';

// IJ-435 · colour each error dot by its owner so fault reads at a glance.
const OWNER_COLOUR = {
  'E1': '#ef4444',
  'Judge': '#f97316',
  'Reply Guard': '#a78bfa',
};
const OWNER_FALLBACK = '#94a3b8';
const ownerColour = (owner) => OWNER_COLOUR[owner] || OWNER_FALLBACK;

function yMaxFor(count) {
  if (!count || count <= 0) return 5;
  return Math.max(5, Math.ceil(count / 5) * 5);
}

// Node x-position: seq 1 (newest) nearest zero, older events run right.
function nodeX(seq, maxCount) {
  const width = PLOT_X_MAX - PLOT_X_MIN;
  const denom = Math.max(maxCount, 1);
  return PLOT_X_MIN + (seq / denom) * width;
}

// Node y-position: cumulative magnitude = seq, climbing away from zero.
function nodeY(seq, ymax, sign) {
  const halfHeight = sign === 'positive' ? ZERO_Y - PLOT_Y_TOP : PLOT_Y_BOT - ZERO_Y;
  const yOffset = (Math.min(seq, ymax) / ymax) * halfHeight;
  return sign === 'positive' ? ZERO_Y - yOffset : ZERO_Y + yOffset;
}

function buildYTicks(ymax, halfSize) {
  const step = Math.max(1, Math.ceil(ymax / halfSize));
  const positive = [];
  const negative = [];
  for (let i = 1; i <= halfSize; i += 1) {
    const value = step * i;
    if (value > ymax) break;
    const yPos = ZERO_Y - (ZERO_Y - PLOT_Y_TOP) * (i / halfSize);
    const yNeg = ZERO_Y + (PLOT_Y_BOT - ZERO_Y) * (i / halfSize);
    positive.push({ value: `+${value}`, y: yPos });
    negative.push({ value: `-${value}`, y: yNeg });
  }
  return { positive, negative };
}

function buildXTicks(maxCount) {
  const width = PLOT_X_MAX - PLOT_X_MIN;
  const ticks = [{ value: '0', x: PLOT_X_MIN }];
  if (!maxCount || maxCount <= 0) return ticks;
  const maxTicks = 10;
  const step = Math.max(1, Math.ceil(maxCount / (maxTicks - 1)));
  for (let n = step; n <= maxCount; n += step) {
    ticks.push({ value: String(n), x: PLOT_X_MIN + (n / maxCount) * width });
  }
  if (ticks[ticks.length - 1].value !== String(maxCount)) {
    ticks.push({ value: String(maxCount), x: PLOT_X_MIN + width });
  }
  return ticks;
}

// One node per event: origin at (PLOT_X_MIN, ZERO_Y), then a dot per item.
function seriesNodes(events, maxCount, ymax, sign) {
  return events.map((ev) => ({
    ...ev,
    x: nodeX(ev.seq, maxCount),
    y: nodeY(ev.seq, ymax, sign),
    signed: sign === 'negative' ? -ev.seq : ev.seq,
  }));
}

function polylineFromOrigin(nodes) {
  const pts = [`${PLOT_X_MIN},${ZERO_Y}`];
  nodes.forEach((n) => pts.push(`${n.x.toFixed(1)},${n.y.toFixed(1)}`));
  return pts.join(' ');
}

function EventDots({ nodes, colour, colourByOwner, seriesKey }) {
  return nodes.map((n) => {
    const fill = colourByOwner ? ownerColour(n.owner) : colour;
    const ownerTag = n.owner ? ` \u00B7 ${n.owner}` : '';
    return (
      <circle
        key={`dot-${seriesKey}-${n.seq}`}
        cx={n.x}
        cy={n.y}
        r="5"
        fill={fill}
        stroke="#0f172a"
        strokeWidth="1"
        style={{ cursor: 'help' }}
        data-testid={`governance-tile-chart-node-${seriesKey}-${n.seq}`}
      >
        <title>{`#${n.seq}${ownerTag} \u00B7 ${n.desc}${n.gb ? ` \u00B7 ${n.gb}` : ''}${n.date ? ` \u00B7 ${n.date}` : ''}`}</title>
      </circle>
    );
  });
}

export default function DirectionChart({ summary, loading, error }) {
  const chartEvents = error ? null : summary?.chart_events;
  const errors = Array.isArray(chartEvents?.errors) ? chartEvents.errors : [];
  const successes = Array.isArray(chartEvents?.successes) ? chartEvents.successes : [];

  const maxCount = Math.max(errors.length, successes.length, 0);
  const ymax = yMaxFor(maxCount);
  const yTicks = buildYTicks(ymax, 6);
  const xTicks = buildXTicks(maxCount);

  const errorNodes = seriesNodes(errors, maxCount, ymax, 'negative');
  const successNodes = seriesNodes(successes, maxCount, ymax, 'positive');
  const hasData = errors.length > 0 || successes.length > 0;

  const titleTail = hasData
    ? ` \u00B7 ${errors.length} error${errors.length === 1 ? '' : 's'}, ${successes.length} success${successes.length === 1 ? '' : 'es'}` + CHART_TITLE_ACTIVE_TAIL_SUFFIX
    : CHART_TITLE_ZERO_TAIL;

  return (
    <section
      data-testid="governance-tile-direction-chart"
      className="rounded-xl border border-slate-800/70 bg-slate-950/40 p-5"
    >
      <div className="flex items-center gap-2 mb-2">
        <h3
          data-testid="governance-tile-direction-chart-title"
          className="text-[13px] font-semibold text-slate-100"
        >
          {CHART_TITLE_PREFIX}
          {titleTail}
        </h3>
        <span
          title={CHART_TITLE_TOOLTIP}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-600 text-[9px] text-slate-400 cursor-help"
        >
          ?
        </span>
      </div>

      <div
        data-testid="governance-tile-direction-chart-explainer"
        className="rounded p-2 mb-3 border-l-2 border-cyan-500 bg-cyan-500/[0.08] text-[11px] text-slate-300 leading-snug"
      >
        <strong className="text-cyan-300">{CHART_EXPLAINER_STRONG}</strong>{' '}
        {CHART_EXPLAINER_BODY}
      </div>

      <div className="relative">
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" height="450" xmlns="http://www.w3.org/2000/svg">
          <rect x={PLOT_X_MIN} y={PLOT_Y_TOP} width={PLOT_X_MAX - PLOT_X_MIN} height={ZERO_Y - PLOT_Y_TOP} fill="rgba(34, 197, 94, 0.03)" />
          <rect x={PLOT_X_MIN} y={ZERO_Y} width={PLOT_X_MAX - PLOT_X_MIN} height={PLOT_Y_BOT - ZERO_Y} fill="rgba(220, 38, 38, 0.03)" />

          <line x1={PLOT_X_MIN} y1={PLOT_Y_TOP} x2={PLOT_X_MIN} y2={PLOT_Y_BOT} stroke="#475569" strokeWidth="1.5" />
          <line x1={PLOT_X_MIN} y1={ZERO_Y} x2={PLOT_X_MAX} y2={ZERO_Y} stroke="#94a3b8" strokeWidth="2" />
          <text x={PLOT_X_MAX + 5} y={ZERO_Y + 4} fill="#94a3b8" fontSize="10" fontWeight="600">
            {CHART_ZERO_LABEL}
          </text>

          <g stroke="#1e293b" strokeWidth="0.5">
            {yTicks.positive.map((t) => (
              <line key={`gp-${t.value}`} x1={PLOT_X_MIN} y1={t.y} x2={PLOT_X_MAX} y2={t.y} />
            ))}
            {yTicks.negative.map((t) => (
              <line key={`gn-${t.value}`} x1={PLOT_X_MIN} y1={t.y} x2={PLOT_X_MAX} y2={t.y} />
            ))}
          </g>

          <g fill="#86efac" fontSize="10" fontFamily="monospace" textAnchor="end">
            <text x={PLOT_X_MIN - 5} y={ZERO_Y + 4}>0</text>
            {yTicks.positive.map((t) => (
              <text key={`lp-${t.value}`} x={PLOT_X_MIN - 5} y={t.y + 4}>{t.value}</text>
            ))}
          </g>
          <g fill="#fca5a5" fontSize="10" fontFamily="monospace" textAnchor="end">
            {yTicks.negative.map((t) => (
              <text key={`ln-${t.value}`} x={PLOT_X_MIN - 5} y={t.y + 4}>{t.value}</text>
            ))}
          </g>

          <text x={20} y={130} fill="#86efac" fontSize="11" fontWeight="700" transform="rotate(-90 20 130)" textAnchor="middle">
            {CHART_Y_LABEL_POS}
          </text>
          <text x={20} y={350} fill="#fca5a5" fontSize="11" fontWeight="700" transform="rotate(-90 20 350)" textAnchor="middle">
            {CHART_Y_LABEL_NEG}
          </text>

          <g fill="#94a3b8" fontSize="10" fontFamily="monospace" textAnchor="middle">
            {xTicks.map((t) => (
              <text key={`x-${t.value}`} x={t.x} y={478}>{t.value}</text>
            ))}
          </g>
          <text x={(PLOT_X_MIN + PLOT_X_MAX) / 2} y={502} fill="#cbd5e1" fontSize="12" fontWeight="600" textAnchor="middle">
            {CHART_X_LABEL}
          </text>

          {/* Success line · climbs above zero · one node per Judge PASS. */}
          {successNodes.length > 0 ? (
            <g data-testid="governance-tile-direction-chart-line-successes">
              <polyline fill="none" stroke={SUCCESS_COLOUR} strokeWidth="3" opacity="0.9" points={polylineFromOrigin(successNodes)} />
              <EventDots nodes={successNodes} colour={SUCCESS_COLOUR} seriesKey="successes" />
              {(() => {
                const last = successNodes[successNodes.length - 1];
                return (
                  <text x={last.x + 6} y={last.y - 4} fill={SUCCESS_COLOUR} fontSize="10" fontWeight="700">
                    {`Successes · +${successes.length}`}
                  </text>
                );
              })()}
            </g>
          ) : null}

          {/* Error line · descends below zero · one node per error/miss/skip. */}
          {errorNodes.length > 0 ? (
            <g data-testid="governance-tile-direction-chart-line-errors">
              <polyline fill="none" stroke={ERROR_COLOUR} strokeWidth="3" opacity="0.9" points={polylineFromOrigin(errorNodes)} />
              <EventDots nodes={errorNodes} colour={ERROR_COLOUR} colourByOwner seriesKey="errors" />
              {(() => {
                const last = errorNodes[errorNodes.length - 1];
                return (
                  <text x={last.x + 6} y={last.y + 4} fill={ERROR_COLOUR} fontSize="10" fontWeight="700">
                    {`Errors · −${errors.length}`}
                  </text>
                );
              })()}
            </g>
          ) : null}
        </svg>

        {hasData ? (
          <div
            data-testid="governance-tile-direction-chart-legend"
            className="absolute top-16 right-4 rounded border border-slate-700/80 bg-slate-950/85 p-2 text-[10px] text-slate-200"
          >
            <p className="mb-1 font-semibold text-slate-300 uppercase tracking-wide">Legend</p>
            <div className="flex items-center gap-2 py-0.5" data-testid="governance-tile-chart-legend-errors">
              <span aria-hidden="true" className="inline-block h-0.5 w-6" style={{ background: ERROR_COLOUR }} />
              <span className="whitespace-nowrap">Errors, misses, skips</span>
              <span className="text-slate-500">(−)</span>
            </div>
            <div className="ml-3 mb-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid="governance-tile-chart-legend-owners">
              <span className="text-[9px] uppercase tracking-wide text-slate-500">by owner:</span>
              {[['E1', OWNER_COLOUR['E1']], ['Judge', OWNER_COLOUR['Judge']], ['Reply Guard', OWNER_COLOUR['Reply Guard']]].map(([name, colour]) => (
                <span key={name} className="flex items-center gap-1" data-testid={`governance-tile-chart-legend-owner-${name.toLowerCase().replace(' ', '-')}`}>
                  <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ background: colour }} />
                  <span className="whitespace-nowrap">{name}</span>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 py-0.5" data-testid="governance-tile-chart-legend-successes">
              <span aria-hidden="true" className="inline-block h-0.5 w-6" style={{ background: SUCCESS_COLOUR }} />
              <span className="whitespace-nowrap">Successes</span>
              <span className="text-slate-500">(+)</span>
            </div>
          </div>
        ) : null}

        {!hasData ? (
          <div
            data-testid="governance-tile-direction-chart-empty"
            className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
          >
            <p className="text-[13px] font-semibold text-slate-200">
              {CHART_EMPTY_STATE_TITLE}
            </p>
            <p className="mt-1 max-w-lg text-[11px] text-slate-400 leading-snug">
              {CHART_EMPTY_STATE_BODY}
            </p>
          </div>
        ) : null}

        {loading ? (
          <p
            data-testid="governance-tile-direction-chart-loading"
            className="mt-2 text-[10px] italic text-slate-500"
          >
            Live series loading&hellip;
          </p>
        ) : null}
        {error ? (
          <p
            data-testid="governance-tile-direction-chart-error"
            className="mt-2 text-[10px] text-rose-400"
          >
            Live series fetch failed. Chart shows shell until the endpoint recovers.
          </p>
        ) : null}
      </div>
    </section>
  );
}
